/**
 * @module usePermissions
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-permissions
 *
 * @remarks
 * Permission management composable with support for RBAC and ABAC patterns.
 *
 * Key features:
 * - Role-Based Access Control (RBAC) support
 * - Attribute-Based Access Control (ABAC) with context
 * - Functional permission conditions
 * - Token-based permission storage
 * - Adapter pattern for custom permission systems
 *
 * Built on createTokens for flexible permission configuration.
 *
 * @example
 * ```ts
 * import { usePermissions } from '@vuetify/v0'
 *
 * const permissions = usePermissions()
 * console.log(permissions.can('admin', 'edit', 'users'))
 * ```
 */

// Composables
import { createPluginContext } from '#v0/composables/createPlugin'
import { createTokens } from '#v0/composables/createTokens'

// Adapters
import { V0PermissionsAdapter } from '#v0/composables/usePermissions/adapters'

// Transformers
import { toArray } from '#v0/composables/toArray'

// Utilities
import { UNSAFE_KEYS } from '#v0/utilities'

// Types
import type { TokenContext, TokenOptions, TokenTicket } from '#v0/composables/createTokens'
import type { PermissionsAdapter } from '#v0/composables/usePermissions/adapters'
import type { ID } from '#v0/types'

// Exports
export { PermissionsAdapter } from '#v0/composables/usePermissions/adapters'

export interface PermissionTicket extends TokenTicket<boolean | ((context: Record<string, unknown>) => boolean)> {}

export interface PermissionContext<Z extends PermissionTicket = PermissionTicket> extends TokenContext<Z> {
  can: (id: ID, action: string, subject: string, context?: Record<string, unknown>) => boolean
  /** Active permissions adapter. */
  adapter: PermissionsAdapter
}

export interface PermissionOptions extends TokenOptions {
  adapter?: PermissionsAdapter
  permissions?: Record<ID, [string | string[], string | string[], (boolean | ((context: Record<string, unknown>) => boolean))?][]>
}

export interface PermissionContextOptions extends PermissionOptions {
  namespace?: string
}

export interface PermissionPluginOptions extends PermissionContextOptions {}

/**
 * Creates a new permissions instance.
 *
 * @param options The options for the permissions instance.
 * @template Z The type of the permission ticket.
 * @template E The type of the permission context.
 * @returns A new permissions instance.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-permissions
 *
 * @example
 * ```ts
 * import { createPermissions } from '@vuetify/v0'
 *
 * const permissions = createPermissions({
 *   permissions: {
 *     admin: [['read', 'users']],
 *     editor: [['edit', 'posts']],
 *   },
 * })
 * ```
 */
export function createPermissions (_options: PermissionOptions = {}): PermissionContext {
  const { adapter = new V0PermissionsAdapter(), permissions = {}, ...options } = _options

  const record: Record<string, Record<string, Record<string, boolean | ((context: Record<string, unknown>) => boolean)>>> = {}
  for (const role in permissions) {
    if (UNSAFE_KEYS.has(role)) continue
    if (!record[role]) record[role] = {}
    for (const [actions, subjects, condition = true] of permissions[role]!) {
      for (const action of toArray(actions)) {
        if (UNSAFE_KEYS.has(action)) continue
        for (const subject of toArray(subjects)) {
          if (UNSAFE_KEYS.has(subject)) continue
          if (!record[role][action]) record[role][action] = {}

          record[role][action][subject] = condition
        }
      }
    }
  }

  const tokens = createTokens(record, options)

  function can (id: ID, action: string, subject: string, context: Record<string, unknown> = {}) {
    return adapter.can(id, action, subject, context, tokens as PermissionContext)
  }

  return {
    ...tokens,
    can,
    adapter,
    get size () {
      return tokens.size
    },
  } as PermissionContext
}

/**
 * Creates a new permissions context.
 *
 * @param options The options for the permissions context.
 * @template Z The type of the permission ticket.
 * @template E The type of the permission context.
 * @returns A new permissions context.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-permissions
 *
 * @example
 * ```ts
 * import { createPermissionsContext } from '@vuetify/v0'
 *
 * export const [usePermissions, providePermissions, context] = createPermissionsContext({
 *   namespace: 'app:permissions',
 *   permissions: {
 *     admin: [['read', 'users'], ['edit', 'users']],
 *     editor: [['edit', 'posts']],
 *   },
 * })
 * ```
 */
function createPermissionsFallback (): PermissionContext {
  return {
    size: 0,
    can: () => false,
  } as unknown as PermissionContext
}

export const [createPermissionsContext, createPermissionsPlugin, usePermissions] =
  /* @__PURE__ */ createPluginContext<PermissionContextOptions, PermissionContext>(
    'v0:permissions',
    options => createPermissions(options),
    {
      fallback: () => createPermissionsFallback(),
      inspect: ctx => ({
        adapter: ctx.adapter.constructor.name,
        collection: ctx.collection,
      }),
    },
  )

/**
 * Creates a new permissions plugin.
 *
 * @param options The options for the permissions plugin.
 * @template Z The type of the permission ticket.
 * @template E The type of the permission context.
 * @returns A new permissions plugin.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-permissions
 *
 * @example
 * ```ts
 * import { createApp } from 'vue'
 * import { createPermissionsPlugin } from '@vuetify/v0'
 * import App from './App.vue'
 *
 * const app = createApp(App)
 *
 * app.use(
 *   createPermissionsPlugin({
 *     permissions: {
 *       admin: [['read', 'users']],
 *       editor: [['edit', 'posts']],
 *     },
 *   })
 * )
 *
 * app.mount('#app')
 * ```
 */
